import { useState } from "react"
import { Section, CodeBlock, InfoBox, FileTree, FileExplanationPanel } from "@/components"
import { nextShadcnProjectStructure } from "@/data/project-structures"
import { nextShadcnFileExplanations, type FileExplanation } from "@/data/file-explanations"

export function NextShadcnArchitecturePage() {
  const [selectedFile, setSelectedFile] = useState<FileExplanation | null>(null)

  const handleFileClick = (filePath: string) => {
    const fileInfo = nextShadcnFileExplanations[filePath]
    if (fileInfo) {
      setSelectedFile(fileInfo)
    }
  }

  return (
    <>
      <div className="space-y-10">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold">Architecture</h1>
          <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
            Understand the Next.js App Router architecture. The template follows best practices 
            with file-based routing, nested layouts, and a clear separation between server and client code.
            Click on any file below to see detailed explanations.
          </p>
        </div>

        <InfoBox type="info" title="Interactive File Explorer">
          Click any file in the tree below to view its purpose, key features, and related files.
        </InfoBox>

        <Section title="Project Structure" description="Interactive file tree">
          <FileTree data={nextShadcnProjectStructure} onFileClick={handleFileClick} />
        </Section>

        <Section title="App Router Pattern" description="File-based routing">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              Next.js App Router uses a file-system based routing where folders define routes 
              and special files define UI for each route segment.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">page.tsx</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Defines the UI for a route. Server Component by default.
                </p>
              </div>
              
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">layout.tsx</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Shared UI wrapper that preserves state and doesn't re-render on navigation.
                </p>
              </div>
              
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">error.tsx</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Error boundary UI. Automatically catches errors and shows fallback.
                </p>
              </div>
            </div>

            <InfoBox type="info" title="Automatic Code Splitting">
              Each route is automatically code split. Only the code needed for the current 
              route is loaded, improving performance.
            </InfoBox>
          </div>
        </Section>

        <Section title="Key Modules" description="Main architectural components">
          <div className="space-y-6">
            <ModuleCard
              title="App Directory"
              description="File-based routing with layouts, pages, and special files"
              features={[
                "Nested layouts and pages",
                "Server Components by default",
                "Error boundaries (error.tsx)",
                "Loading states (loading.tsx)",
                "Not found pages (not-found.tsx)",
              ]}
            />

            <ModuleCard
              title="Components"
              description="Organized into UI (Shadcn), common, layout, dashboard, and views"
              features={[
                "Shadcn UI components with 'use client'",
                "Server and Client components",
                "Dashboard components (MetricCard, DataTable)",
                "Common components (ErrorBoundary, Modal)",
                "View components for page content",
              ]}
            />

            <ModuleCard
              title="Services (Client-Side)"
              description="API communication for client components"
              features={[
                "Axios client with interceptors",
                "Auth service for authentication",
                "Client-side only (uses 'use client')",
                "Request/response interceptors",
              ]}
            />

            <ModuleCard
              title="State Management"
              description="Zustand for client-side state"
              features={[
                "AuthStore for user/token (client-side)",
                "ThemeStore for dark/light mode",
                "SidebarStore for sidebar state",
                "Persistent storage with localStorage",
              ]}
            />

            <ModuleCard
              title="Server vs Client"
              description="Clear separation between server and client code"
              features={[
                "Server Components for static content",
                "Client Components marked with 'use client'",
                "Server Actions for mutations",
                "API Route Handlers for endpoints",
              ]}
            />
          </div>
        </Section>

        <Section title="Routing Examples" description="Common patterns">
          <div className="space-y-8">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Route (Server Component)</h3>
              <CodeBlock
                code={`// app/about/page.tsx
export default function AboutPage() {
  return (
    <div>
      <h1>About</h1>
      <p>This is the about page (Server Component)</p>
    </div>
  )
}

// Accessible at: /about`}
                language="typescript"
                filename="app/about/page.tsx"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Dynamic Route</h3>
              <CodeBlock
                code={`// app/blog/[slug]/page.tsx
interface PageProps {
  params: Promise<{ slug: string }>
}

export default async function BlogPost({ params }: PageProps) {
  const { slug } = await params
  
  // Fetch data on the server
  const post = await fetch(\`https://api.example.com/posts/\${slug}\`)
    .then(res => res.json())
  
  return (
    <article>
      <h1>{post.title}</h1>
      <p>{post.content}</p>
    </article>
  )
}

// Accessible at: /blog/my-post`}
                language="typescript"
                filename="app/blog/[slug]/page.tsx"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Nested Layout</h3>
              <CodeBlock
                code={`// app/dashboard/layout.tsx
'use client'

import { AppLayout } from "@/components/layout/AppLayout"

export default function DashboardLayout({
  children
}: {
  children: React.ReactNode
}) {
  return <AppLayout>{children}</AppLayout>
}

// This layout wraps all /dashboard/* pages`}
                language="typescript"
                filename="app/dashboard/layout.tsx"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Client Component</h3>
              <CodeBlock
                code={`// app/chat/page.tsx
'use client'

import { useState } from "react"
import { ChatView } from "@/components/views/ChatView"

export default function ChatPage() {
  const [selectedChat, setSelectedChat] = useState(null)
  
  return (
    <ChatView 
      selectedChat={selectedChat}
      onSelectChat={setSelectedChat}
    />
  )
}

// Must use 'use client' for interactivity`}
                language="typescript"
                filename="app/chat/page.tsx"
              />
            </div>
          </div>
        </Section>

        <Section title="Server vs Client Components" description="When to use each">
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card border rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-lg">Server Components</h3>
                <p className="text-sm text-muted-foreground">Use for:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>Data fetching</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>Direct database/API access</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>Static content</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>Sensitive data handling</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card border rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-lg">Client Components</h3>
                <p className="text-sm text-muted-foreground">Use for:</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>Interactive elements</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>Event listeners (onClick, etc.)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>React hooks (useState, useEffect)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">→</span>
                    <span>Browser-only APIs</span>
                  </li>
                </ul>
              </div>
            </div>

            <InfoBox type="warning" title="'use client' Directive">
              Add <code className="bg-muted px-2 py-1 rounded">'use client'</code> at the top of files 
              that need client-side interactivity. All Shadcn UI components and interactive features 
              require this directive.
            </InfoBox>
          </div>
        </Section>

        <Section title="Service Layer" description="Client-side API communication">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              Services handle client-side API communication. They can only be used in Client Components.
            </p>
            
            <CodeBlock
              code={`// src/services/api/client.ts
import axios from 'axios'

export const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = \`Bearer \${token}\`
    }
    return config
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 401, refresh token, retry, etc.
    return Promise.reject(error)
  }
)`}
              language="typescript"
              filename="src/services/api/client.ts"
            />
          </div>
        </Section>

        <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
          <h2 className="text-2xl font-bold">Best Practices</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Use Server Components by default, Client Components when needed</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Keep 'use client' as low in the component tree as possible</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Fetch data in Server Components for better performance</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Use loading.tsx for instant loading states</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Implement error.tsx for graceful error handling</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Leverage automatic code splitting for performance</span>
            </li>
          </ul>
        </div>
      </div>

      <FileExplanationPanel 
        file={selectedFile} 
        onClose={() => setSelectedFile(null)} 
      />
    </>
  )
}

function ModuleCard({
  title,
  description,
  features
}: {
  title: string
  description: string
  features: string[]
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div>
        <h3 className="font-semibold text-lg mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold mb-3">Key Features:</h4>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex gap-2 text-sm text-muted-foreground">
              <span className="text-primary">→</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
